import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2 } from "lucide-react";
import ContactMultiStepForm from "./ContactMultiStepForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_OPTIONS = [
  "BBA",
  "MBA",
  "Executive MBA",
];

const LEVEL_OPTIONS = ["Bac", "Bac+2", "Bac+3", "Bac+4", "Bac+5", "Cadre en activité"];

export const ContactSection = () => {
  const location = useLocation();
  const [tab, setTab] = useState("contact");
  const formRef = useRef(null);
  const [appForm, setAppForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", program: "", education_level: "", motivation: "", nationality: "",
  });
  const [loading, setLoading] = useState(false);

  // Activate "Candidater" tab + scroll when URL ends with #candidater
  useEffect(() => {
    if (location.hash === "#candidater") {
      setTab("apply");
      const t = setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!appForm.program) { toast.error("Veuillez choisir un programme"); return; }
    if (!appForm.education_level) { toast.error("Veuillez indiquer votre niveau"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, appForm);
      toast.success("Candidature reçue !", { description: "Réponse sous 10 jours ouvrés." });
      setAppForm({ first_name: "", last_name: "", email: "", phone: "", program: "", education_level: "", motivation: "", nationality: "" });
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail?.[0]?.msg || "Veuillez réessayer." });
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-padding bg-white">
      <div className="container-edge">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="overline mb-4">Contact & Campus</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight mb-6">
              Parlons<br /> de votre <em className="text-navy-500">avenir</em>.
            </h2>
            <p className="text-[#555] mb-10 leading-relaxed">
              Une question, une candidature, un partenariat ? Nous vous répondons
              en moins de 48 heures.
            </p>

            {/* Stylized map */}
            <div className="relative aspect-[4/3] bg-[#FAF8F3] mb-10 overflow-hidden border border-navy-100">
              <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
                <defs>
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(0,35,98,0.10)" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="400" height="300" fill="url(#grid)" />
                {/* abstract roads */}
                <path d="M0 180 Q120 130 200 160 T 400 140" stroke="rgba(237,41,57,0.4)" strokeWidth="2" fill="none" />
                <path d="M0 80 Q150 90 230 60 T 400 100" stroke="rgba(237,41,57,0.25)" strokeWidth="1.5" fill="none" />
                <path d="M120 0 L 180 300" stroke="rgba(0,35,98,0.15)" strokeWidth="1" fill="none" />
                <path d="M280 0 L 240 300" stroke="rgba(0,35,98,0.15)" strokeWidth="1" fill="none" />
                {/* abstract blocks */}
                <rect x="60" y="110" width="40" height="30" fill="rgba(0,35,98,0.05)" />
                <rect x="260" y="180" width="60" height="40" fill="rgba(0,35,98,0.05)" />
                <rect x="180" y="40" width="30" height="20" fill="rgba(0,35,98,0.05)" />
              </svg>
              {/* Pin */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-50" />
                  <span className="relative block w-5 h-5 rounded-full bg-red-500 ring-4 ring-red-500/20" />
                </div>
                <div className="mt-3 px-3 py-1 bg-navy-700 text-white text-[10px] uppercase tracking-[0.2em] whitespace-nowrap">
                  ECL Campus Abidjan
                </div>
              </div>
            </div>

            <ul className="space-y-4 text-[#444]">
              <li className="flex items-start gap-3" data-testid="contact-info-address">
                <MapPin className="h-5 w-5 text-navy-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-navy-700 font-semibold text-sm uppercase tracking-wider mb-1">Campus</div>
                  157 Rue Sonhmon (Pharmacie Y4)<br />
                  <span className="text-[#888] text-sm">Abidjan, Côte d'Ivoire</span>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="contact-info-phone">
                <Phone className="h-5 w-5 text-navy-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-navy-700 font-semibold text-sm uppercase tracking-wider mb-1">Téléphone</div>
                  +225 01 04 41 94 06<br />
                  <span className="text-[#888] text-sm">+225 07 49 54 45 02</span>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="contact-info-email">
                <Mail className="h-5 w-5 text-navy-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-navy-700 font-semibold text-sm uppercase tracking-wider mb-1">Email</div>
                  contact@ecoledecommercedelyon.ci
                </div>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
            ref={formRef}
            id="candidater"
          >
            <Tabs value={tab} onValueChange={setTab} className="w-full scroll-mt-28">
              <TabsList className="grid grid-cols-2 w-full bg-[#F7F6F2] rounded-none h-auto p-1 mb-8">
                <TabsTrigger
                  value="contact"
                  className="rounded-none data-[state=active]:bg-navy-700 data-[state=active]:text-white text-navy-700 py-3 uppercase tracking-wider text-xs"
                  data-testid="tab-contact"
                >
                  Nous contacter
                </TabsTrigger>
                <TabsTrigger
                  value="apply"
                  className="rounded-none data-[state=active]:bg-navy-500 data-[state=active]:text-white text-navy-700 py-3 uppercase tracking-wider text-xs"
                  data-testid="tab-apply"
                >
                  Candidater
                </TabsTrigger>
              </TabsList>

              <TabsContent value="contact" className="mt-0">
                <ContactMultiStepForm />
              </TabsContent>

              <TabsContent value="apply" className="mt-0">
                <form onSubmit={submitApplication} className="space-y-5" data-testid="application-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="a-first" className="text-navy-700 text-xs uppercase tracking-wider">Prénom *</Label>
                      <Input
                        id="a-first" required
                        value={appForm.first_name}
                        onChange={(e) => setAppForm({ ...appForm, first_name: e.target.value })}
                        className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500"
                        data-testid="apply-input-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="a-last" className="text-navy-700 text-xs uppercase tracking-wider">Nom *</Label>
                      <Input
                        id="a-last" required
                        value={appForm.last_name}
                        onChange={(e) => setAppForm({ ...appForm, last_name: e.target.value })}
                        className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500"
                        data-testid="apply-input-last-name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="a-email" className="text-navy-700 text-xs uppercase tracking-wider">Email *</Label>
                      <Input
                        id="a-email" type="email" required
                        value={appForm.email}
                        onChange={(e) => setAppForm({ ...appForm, email: e.target.value })}
                        className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500"
                        data-testid="apply-input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="a-phone" className="text-navy-700 text-xs uppercase tracking-wider">Téléphone *</Label>
                      <Input
                        id="a-phone" required
                        value={appForm.phone}
                        onChange={(e) => setAppForm({ ...appForm, phone: e.target.value })}
                        className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500"
                        data-testid="apply-input-phone"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="a-program" className="text-navy-700 text-xs uppercase tracking-wider">Programme visé *</Label>
                      <Select
                        value={appForm.program}
                        onValueChange={(v) => setAppForm({ ...appForm, program: v })}
                      >
                        <SelectTrigger
                          id="a-program"
                          className="mt-2 rounded-none border-navy-100 focus:ring-navy-500 focus:ring-offset-0"
                          data-testid="apply-select-program"
                        >
                          <SelectValue placeholder="Choisir un programme" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {PROGRAM_OPTIONS.map((p) => (
                            <SelectItem key={p} value={p} className="rounded-none" data-testid={`apply-program-option-${p.replace(/\s/g, "-")}`}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="a-level" className="text-navy-700 text-xs uppercase tracking-wider">Niveau actuel *</Label>
                      <Select
                        value={appForm.education_level}
                        onValueChange={(v) => setAppForm({ ...appForm, education_level: v })}
                      >
                        <SelectTrigger
                          id="a-level"
                          className="mt-2 rounded-none border-navy-100 focus:ring-navy-500 focus:ring-offset-0"
                          data-testid="apply-select-level"
                        >
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none">
                          {LEVEL_OPTIONS.map((l) => (
                            <SelectItem key={l} value={l} className="rounded-none">{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="a-nat" className="text-navy-700 text-xs uppercase tracking-wider">Nationalité</Label>
                    <Input
                      id="a-nat"
                      value={appForm.nationality}
                      onChange={(e) => setAppForm({ ...appForm, nationality: e.target.value })}
                      className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500"
                      data-testid="apply-input-nationality"
                    />
                  </div>

                  <div>
                    <Label htmlFor="a-mot" className="text-navy-700 text-xs uppercase tracking-wider">Lettre de motivation *</Label>
                    <Textarea
                      id="a-mot" required rows={6}
                      placeholder="Pourquoi rejoindre l'ECL Campus Abidjan ? Vos ambitions, votre projet…"
                      value={appForm.motivation}
                      onChange={(e) => setAppForm({ ...appForm, motivation: e.target.value })}
                      className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500 resize-none"
                      data-testid="apply-input-motivation"
                    />
                  </div>

                  <p className="text-[#888] text-xs">
                    Le CV pourra être transmis par email après validation initiale de votre candidature.
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-red w-full md:w-auto disabled:opacity-60"
                    data-testid="apply-submit-button"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {loading ? "Envoi…" : "Envoyer ma candidature"}
                  </button>
                </form>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
